// Shared toon shading for props (trees, decorations, village, boats).
//
// A gradient map is a 1-D lookup on light intensity, so lighting lands in a
// few flat bands instead of a smooth falloff — the illustrative read the
// watercolour terrain wants, and the thing that stops low-poly props from
// looking like plastic. Three samples only the RED channel of this texture,
// so the ramp is greyscale by definition; warm or cool shadows have to come
// from the scene lights, not from here.

import * as THREE from 'three';

// Band values, dark to light. Kept high and close together on purpose: the
// watercolour reference barely shades form at all, so surfaces should read
// as near-flat painted fills with only a hint of turn-away darkening.
// Spreading these back out (e.g. 0x7a..0xff) returns to a cel-shaded look.
const RAMP_STEPS = [0xcc, 0xe6, 0xff];

// Props are smooth-shaded, which is what we want — watercolour foliage
// reads as soft masses, and faceting is most of what made the low-poly
// primitives look like a voxel game. Note there's no choice about it:
// MeshToonMaterial has no flatShading (that's Phong/Lambert/Standard only),
// so getting the faceted look back would mean changing material type.

function makeRamp(): THREE.DataTexture {
  const data = new Uint8Array(RAMP_STEPS.length * 4);
  RAMP_STEPS.forEach((v, i) => {
    data[i * 4] = v; data[i * 4 + 1] = v; data[i * 4 + 2] = v; data[i * 4 + 3] = 255;
  });
  const tex = new THREE.DataTexture(data, RAMP_STEPS.length, 1, THREE.RGBAFormat);
  // Linear filtering would smooth the bands away and defeat the point.
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

const ramp = makeRamp();

export function toonMat(color: number, emissive = 0x000000): THREE.MeshToonMaterial {
  return new THREE.MeshToonMaterial({ color, emissive, gradientMap: ramp });
}

// For InstancedMesh pools that carry per-instance colour (ground cover).
export function toonVertexColorMat(): THREE.MeshToonMaterial {
  return new THREE.MeshToonMaterial({ vertexColors: true, gradientMap: ramp });
}

// ================= Ink outlines =================
// Inverted-hull outlines: a slightly enlarged copy of each mesh, rendered
// back-faces-only in a flat dark colour, so it shows as a drawn line around
// the silhouette. This is the "sketched" half of the look — in the
// reference art the ink line carries the form and the wash just fills it.
//
// Cost is one extra mesh per prop mesh (geometry is shared, so it's draw
// calls, not memory). Set OUTLINES = false to turn the whole thing off.
const OUTLINES = true;
// World units, not a percentage — see inflate(). Exported so the instanced
// pools in chunks.ts can build their own outline pass at the same weight.
export const OUTLINE_THICKNESS = 0.0035;
const outlineMat = new THREE.MeshBasicMaterial({
  color: 0x40382e,
  side: THREE.BackSide,
  polygonOffset: true,
  polygonOffsetFactor: 1,
  polygonOffsetUnits: 1,
});

// Scale factor that grows one axis by a constant `t` of world space on each
// side, rather than by a percentage of its size.
//
// A flat percentage is what makes thin parts flicker: scaling a 12mm noren
// panel by 1.045 moves its surface half a millimetre, so the outline lands
// on top of the original and the two z-fight. Deriving the factor from the
// geometry's actual size gives every part the same ink weight instead.
function inflate(sizeLocal: number, meshScale: number, t: number): number {
  const world = Math.abs(sizeLocal * meshScale);
  return world < 1e-6 ? 1 : (world + 2 * t) / world;
}

// Adds an outline to every mesh under `root`. Meshes are collected before
// any are added, since the outlines are themselves meshes and would
// otherwise be picked up by the same traversal.
export function addOutlines(root: THREE.Object3D, thickness = OUTLINE_THICKNESS): void {
  if (!OUTLINES) return;
  const meshes: THREE.Mesh[] = [];
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh && !o.userData.isOutline) meshes.push(m);
  });
  const size = new THREE.Vector3();
  meshes.forEach((m) => {
    if (!m.geometry.boundingBox) m.geometry.computeBoundingBox();
    m.geometry.boundingBox!.getSize(size);
    const o = new THREE.Mesh(m.geometry, outlineMat);
    o.scale.set(
      inflate(size.x, m.scale.x, thickness),
      inflate(size.y, m.scale.y, thickness),
      inflate(size.z, m.scale.z, thickness),
    );
    o.userData.isOutline = true;
    m.add(o);
  });
}
